/* eslint-disable jsx-a11y/no-autofocus -- intentional UX: dialogs/inputs need immediate focus */
/**
 * InvoiceModals — void confirm, post confirm, write-off confirm,
 * payment modal, and version history modal.
 */

import { Modal, Button, Input, DatePicker } from '@elsapiens/ui'
import { AlertCircle } from 'lucide-react'

import { RecordPaymentModal } from '../../components/billing'
import { InvoiceVersionHistory } from '../../components/billing/InvoiceVersionHistory'
import { formatAmount } from '../../types/billing'
import { parseLocalDate, toLocalDateStr } from '../../utils/formatters'

import type { Invoice } from '../../types/billing'

// ── Post Confirmation Modal ───────────────────────────────────────────────────

interface PostConfirmModalProps {
  isOpen: boolean
  isVendor: boolean
  isLoading: boolean
  invoiceToPost: Invoice | null
  postingDate: string
  onPostingDateChange: (d: string) => void
  onConfirm: () => void
  onClose: () => void
}

export function PostConfirmModal({
  isOpen,
  isVendor,
  isLoading,
  invoiceToPost,
  postingDate,
  onPostingDateChange,
  onConfirm,
  onClose,
}: PostConfirmModalProps): JSX.Element {
  const label = isVendor ? 'Bill' : 'Invoice'
  return (
    <Modal
      description={`Are you sure you want to post ${isVendor ? 'bill' : 'invoice'} ${invoiceToPost?.invoiceNumber}?`}
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={isLoading || !postingDate} size="sm" variant="primary" onClick={onConfirm}>
            {`Post ${label}`}
          </Button>
        </>
      }
      isOpen={isOpen}
      size="sm"
      title={`Post ${label}`}
      onClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <p>This action cannot be undone. The invoice will be finalized and a journal entry will be created.</p>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span style={{ fontSize: '13px', fontWeight: 500 }}>{isVendor ? 'Bill Date' : 'Invoice Date'}</span>
          <DatePicker
            value={postingDate ? parseLocalDate(postingDate) : null}
            onChange={(d) => onPostingDateChange(d ? toLocalDateStr(d) : '')}
          />
        </label>
      </div>
    </Modal>
  )
}

// ── Post Confirmation Modal (list view — no date picker) ─────────────────────

interface PostConfirmSimpleModalProps {
  isOpen: boolean
  isVendor: boolean
  isLoading: boolean
  invoiceToPost: Invoice | null
  onConfirm: () => void
  onClose: () => void
}

export function PostConfirmSimpleModal({
  isOpen,
  isVendor,
  isLoading,
  invoiceToPost,
  onConfirm,
  onClose,
}: PostConfirmSimpleModalProps): JSX.Element {
  const label = isVendor ? 'Bill' : 'Invoice'
  return (
    <Modal
      description={`Are you sure you want to post ${isVendor ? 'bill' : 'invoice'} ${invoiceToPost?.invoiceNumber}?`}
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={isLoading} size="sm" variant="primary" onClick={onConfirm}>
            {`Post ${label}`}
          </Button>
        </>
      }
      isOpen={isOpen}
      size="sm"
      title={`Post ${label}`}
      onClose={onClose}
    >
      <p>This action cannot be undone. The invoice will be finalized and a journal entry will be created.</p>
    </Modal>
  )
}

// ── Void Modal ────────────────────────────────────────────────────────────────

interface VoidModalProps {
  isOpen: boolean
  isVendor: boolean
  isLoading: boolean
  invoiceToVoid: Invoice | null
  voidReason: string
  onVoidReasonChange: (r: string) => void
  onConfirm: () => void
  onClose: () => void
}

export function VoidModal({
  isOpen,
  isVendor,
  isLoading,
  invoiceToVoid,
  voidReason,
  onVoidReasonChange,
  onConfirm,
  onClose,
}: VoidModalProps): JSX.Element {
  const label = isVendor ? 'Bill' : 'Invoice'
  return (
    <Modal
      description={`Enter reason for voiding ${isVendor ? 'bill' : 'invoice'} ${invoiceToVoid?.invoiceNumber}`}
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={isLoading || !voidReason.trim()} size="sm" variant="destructive" onClick={onConfirm}>
            {`Void ${label}`}
          </Button>
        </>
      }
      isOpen={isOpen}
      size="sm"
      title={`Void ${label}`}
      onClose={onClose}
    >
      <div className="el-space-y-md">
        <p className="text-sm text-foreground-secondary">This action cannot be undone.</p>
        <Input
          autoFocus
          placeholder="Enter reason for voiding..."
          value={voidReason}
          onChange={(e) => onVoidReasonChange(e.target.value)}
        />
      </div>
    </Modal>
  )
}

// ── Write-Off Confirmation Modal ──────────────────────────────────────────────

interface WriteOffModalProps {
  isOpen: boolean
  isWriteOffSaving: boolean
  writeOffType: 'rounding' | 'bad_debt'
  writeOffNote: string
  outstandingBalance: number
  invoiceCurrency: string
  onNoteChange: (n: string) => void
  onConfirm: () => void
  onClose: () => void
}

function WriteOffFooter({ onClose, onConfirm, isSaving, writeOffType, writeOffNote, confirmLabel }: {
  onClose: () => void
  onConfirm: () => void
  isSaving: boolean
  writeOffType: 'rounding' | 'bad_debt'
  writeOffNote: string
  confirmLabel: string
}): JSX.Element {
  return (
    <>
      <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
      <Button
        disabled={isSaving || (writeOffType === 'bad_debt' && !writeOffNote.trim())}
        size="sm"
        variant={writeOffType === 'bad_debt' ? 'destructive' : 'primary'}
        onClick={onConfirm}
      >
        {confirmLabel}
      </Button>
    </>
  )
}

export function WriteOffModal({
  isOpen,
  isWriteOffSaving,
  writeOffType,
  writeOffNote,
  outstandingBalance,
  invoiceCurrency,
  onNoteChange,
  onConfirm,
  onClose,
}: WriteOffModalProps): JSX.Element {
  const confirmLabel = resolveWriteOffConfirmLabel(isWriteOffSaving, writeOffType)
  const ContentComponent = writeOffType === 'rounding' ? RoundingContent : BadDebtContent
  return (
    <Modal
      description={`Write off ${formatAmount(outstandingBalance, invoiceCurrency)} as ${writeOffType === 'rounding' ? 'rounding adjustment' : 'bad debt'}?`}
      footer={
        <WriteOffFooter
          confirmLabel={confirmLabel}
          isSaving={isWriteOffSaving}
          writeOffNote={writeOffNote}
          writeOffType={writeOffType}
          onClose={onClose}
          onConfirm={onConfirm}
        />
      }
      isOpen={isOpen}
      size="sm"
      title={writeOffType === 'rounding' ? 'Round Off Balance' : 'Write Off as Bad Debt'}
      onClose={() => { onClose(); onNoteChange('') }}
    >
      <ContentComponent
        invoiceCurrency={invoiceCurrency}
        outstandingBalance={outstandingBalance}
        writeOffNote={writeOffNote}
        onNoteChange={onNoteChange}
      />
    </Modal>
  )
}

function resolveWriteOffConfirmLabel(isSaving: boolean, type: 'rounding' | 'bad_debt'): string {
  if (isSaving) { return 'Processing...' }
  if (type === 'bad_debt') { return 'Write Off as Bad Debt' }
  return 'Confirm Round Off'
}

interface WriteOffContentProps {
  outstandingBalance: number
  invoiceCurrency: string
  writeOffNote: string
  onNoteChange: (n: string) => void
}

function RoundingContent({ outstandingBalance, invoiceCurrency, writeOffNote, onNoteChange }: WriteOffContentProps): JSX.Element {
  return (
    <div className="flex flex-col el-gap-sm">
      <p className="text-sm text-foreground-secondary">
        This will create a rounding adjustment entry to clear the remaining balance of {formatAmount(outstandingBalance, invoiceCurrency)}.
      </p>
      <Input
        placeholder="Note (optional)"
        value={writeOffNote}
        onChange={(e) => onNoteChange(e.target.value)}
      />
    </div>
  )
}

function BadDebtContent({ outstandingBalance, invoiceCurrency, writeOffNote, onNoteChange }: WriteOffContentProps): JSX.Element {
  return (
    <div className="flex flex-col el-gap-sm">
      <div className="flex gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
        <div className="text-sm text-red-700">
          <strong>This action is irreversible.</strong>{' '}
          The outstanding balance of {formatAmount(outstandingBalance, invoiceCurrency)} will be
          written off as a bad debt expense. This should only be used for amounts that are
          confirmed uncollectible.
        </div>
      </div>
      <Input
        autoFocus
        placeholder="Reason for write-off (required)"
        value={writeOffNote}
        onChange={(e) => onNoteChange(e.target.value)}
      />
    </div>
  )
}

// ── Version History Modal ─────────────────────────────────────────────────────

interface VersionHistoryModalProps {
  invoiceId: string
  orgId: string
  isOpen: boolean
  onClose: () => void
}

export function VersionHistoryModal({ invoiceId, orgId, isOpen, onClose }: VersionHistoryModalProps): JSX.Element {
  return (
    <InvoiceVersionHistory
      invoiceId={invoiceId}
      isOpen={isOpen}
      orgId={orgId}
      onClose={onClose}
    />
  )
}

// ── Record Payment Modal wrapper ─────────────────────────────────────────────

interface PaymentModalProps {
  invoice: Invoice
  organizationId: string
  ledgerId: string
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export function PaymentModal({ invoice, organizationId, ledgerId, isOpen, onClose, onSuccess }: PaymentModalProps): JSX.Element {
  return (
    <RecordPaymentModal
      invoice={invoice}
      isOpen={isOpen}
      ledgerId={ledgerId}
      organizationId={organizationId}
      onClose={onClose}
      onSuccess={onSuccess}
    />
  )
}
